import { useState, ReactElement, useEffect } from 'react';
import { useRouter } from 'next/router';
import { NextPageContext } from 'next';
import { stringify, ParsedUrlQuery } from 'querystring';
import { isEmpty } from 'lodash';
import { getCurrentUserFromContext } from 'server/api/services/auth';
import { ListService } from 'server/api/services/lists/creatorListService';
import { slugToString } from 'lib/utils/filterUtils';
import { creatorFilterDescription, pluralizeUserType, pluralizeRole } from 'lib/filters';
import { serialize } from 'lib/helpers';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { CreatorLinksFooter } from 'components/CreatorLinksFooter';
import { CreatorLinkService } from 'server/api/services/creatorLinkService';
import { CreatorLink, Location } from 'lib/types';
import { queryToCriteria } from 'features/Creators/utils';
import { webAssetUrl } from 'lib/utils/imageUtils';
import { Creators } from 'features/Creators';
import { CreatorsListUser } from 'lib/types/users';
import { ESServiceMeta } from 'server/api/types';
import { CreatorSearchCriteria } from 'server/api/services/creatorSearch/types';
import { UserCreditsService } from 'server/api/services/creatorSearch/userCredits/userCredits';
import { geoSlugToLocation } from 'lib/utils/modelSlugUtils';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { TeamsProvider } from 'lib/contexts/teamsContext';
import { UserListsProvider } from 'lib/contexts/userListsContext';

function redirectToCreators() {
  return {
    redirect: {
      destination: '/creators',
      permanent: false,
    },
  };
}

const noQuery = (query: ParsedUrlQuery) => {
  const except = ['userType', 'page', 'view'];
  const keys = Object.keys(query).filter((k) => !except.includes(k));
  return keys.length <= 0;
};

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;
  const currentUser = await getCurrentUserFromContext(context) as any;

  const linkService = new CreatorLinkService({ currentUser });
  const criteria = queryToCriteria(query);

  let creators: CreatorsListUser[] = [];
  let meta: ESServiceMeta | undefined;
  if (!currentUser) {
    try {
      const response = await UserCreditsService
        .asUser(currentUser)
        .search(criteria);
      meta = response.meta;
      creators = [];
    } catch (err) {
      // noop
    }
  }
  const creatorLinks = await linkService.getFooterLinks(
    criteria.byRoleId || [], query.geo as string,
  );

  const location = query.geo ? geoSlugToLocation(query.geo as string) : null;
  if (query.geo && !location) {
    // If the geo query is invalid, redirect to the creators page
    return redirectToCreators();
  }
  const role = () => {
    if (typeof query.role === 'string') return pluralizeRole(query.role.split('~')[0]);
    if (query?.role) return pluralizeRole(query?.role[0].split('~')[0]);
    return 'Creators';
  };

  if (query?.list) {
    const ls = new ListService({ currentUser });

    try {
      // check if the list is a public list
      const listId = await ListService.fetchListIdFromPublicId(
        slugToString(query?.list as string) as string,
      );
      if (!listId) {
        return redirectToCreators();
      }
      await ls.ensureCurrentUserCanView(listId);
    } catch (err) {
      return redirectToCreators();
    }
  }

  const baseUrl = 'https://gondola.cc/creators';
  const url = !isEmpty(query) ? `${baseUrl}?${stringify(query)}` : baseUrl;

  const creatorType = pluralizeUserType(query.userType as string);
  if (!creatorLinks) {
    return {
      props: {
        title: `Search for ${creatorType}`,
        url,
        moreRoles: [],
        moreLocations: [],
        location: null,
        role: null,
        criteria: serialize(criteria),
        creators: serialize(creators),
        meta: serialize(meta),
      },
    };
  }
  if (noQuery(query)) {
    return {
      props: {
        title: `Search for ${creatorType}`,
        url,
        moreRoles: serialize(creatorLinks.moreRoles),
        moreLocations: serialize(creatorLinks.moreLocations),
        location,
        role: role(),
        criteria: serialize(criteria),
        creators: serialize(creators),
        meta: serialize(meta),
      },
    };
  }

  return {
    props: {
      title: creatorFilterDescription(query, creatorType),
      url,
      moreRoles: serialize(creatorLinks.moreRoles),
      moreLocations: serialize(creatorLinks.moreLocations),
      location: serialize(location),
      role: role(),
      criteria: serialize(criteria),
      creators: serialize(creators),
      meta: serialize(meta),
    },
  };
};

interface Props {
  title: string;
  url: string;
  moreRoles: CreatorLink[];
  moreLocations: CreatorLink[];
  location?: Partial<Location>;
  role: string;
  creators?: CreatorsListUser[];
  meta?: ESServiceMeta;
  criteria?: CreatorSearchCriteria;
}

const CreatorsPage = ({
  title, url, moreRoles, moreLocations, location, role, creators, meta, criteria,
}: Props) => {
  const router = useRouter();
  const [richTitle, setRichTitle] = useState<string|ReactElement>('');
  const [_title, setTitle] = useState<string>(title);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const creatorType = pluralizeUserType(router.query.userType as string);
    if (noQuery(router.query)) {
      const noQueryTitle = `Search for ${creatorType}`;
      setRichTitle(noQueryTitle);
      return;
    }
    setTitle(creatorFilterDescription(router.query, creatorType, false) as string);
    setRichTitle(creatorFilterDescription(router.query, creatorType, true));
  }, [router]);

  return (
    <>
      <GondolaHead
        title={_title || 'Find creators on Gondola'}
        description={
          `Search for creators by role, location, collaborators, keyword, and more. ${_title}`
        }
        url={url}
        cardUrl={webAssetUrl('meta-data-cards/creators.png')}
      />
      <RolesProvider>
        <UserListsProvider includeUsers>
          <TeamsProvider>
            <Creators
              title={_title}
              richTitle={richTitle}
              serverCreators={creators}
              serverMeta={meta}
              serverCriteria={criteria}
            />
          </TeamsProvider>
        </UserListsProvider>
      </RolesProvider>
      <CreatorLinksFooter
        location={location}
        role={role || ''}
        locationName={location?.name || ''}
        roleName={role || ''}
        moreRoles={moreRoles || []}
        moreLocations={moreLocations || []}
      />
    </>
  );
};

export default CreatorsPage;
